import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const BUSINESSES = [
  {
    name: 'NovaTech Solutions',
    sector: 'FinTech',
    stage: 'Series A',
    emoji: '💻',
    description: 'AI-powered financial literacy platform for underserved communities.',
  },
  {
    name: 'GreenRoot Farms',
    sector: 'AgriTech',
    stage: 'Seed',
    emoji: '🌱',
    description: 'Sustainable urban farming network connecting local producers to consumers.',
  },
  {
    name: 'Luminary Health',
    sector: 'HealthTech',
    stage: 'Pre-Seed',
    emoji: '🏥',
    description: 'Telehealth platform focused on maternal health for women of color.',
  },
];

const COURSES = [
  { title: 'Investing 101', lessons: 12, level: 'Beginner', progress: 0, emoji: '📖' },
  { title: 'Generational Wealth', lessons: 18, level: 'Intermediate', progress: 0, emoji: '🏛️' },
  { title: 'Real Estate Basics', lessons: 15, level: 'Beginner', progress: 0, emoji: '🏠' },
  { title: 'Venture Capital', lessons: 20, level: 'Advanced', progress: 0, emoji: '🚀' },
];

const LEVEL_COLORS = {
  Beginner: 'bg-emerald-100 text-emerald-700',
  Intermediate: 'bg-blue-100 text-blue-700',
  Advanced: 'bg-purple-100 text-purple-700',
};

const ShowcaseSection = () => {
  const showcaseRef = useRef(null);
  const academyRef = useRef(null);
  const [showcaseVisible, setShowcaseVisible] = useState(false);
  const [academyVisible, setAcademyVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const obs1 = new IntersectionObserver(([e]) => { if (e.isIntersecting) setShowcaseVisible(true); }, { threshold: 0.1 });
    const obs2 = new IntersectionObserver(([e]) => { if (e.isIntersecting) setAcademyVisible(true); }, { threshold: 0.1 });
    if (showcaseRef?.current) obs1?.observe(showcaseRef?.current);
    if (academyRef?.current) obs2?.observe(academyRef?.current);
    return () => { obs1?.disconnect(); obs2?.disconnect(); };
  }, []);

  return (
    <>
      {/* Women-Led Showcase */}
      <section className="py-8 sm:py-14 bg-white" ref={showcaseRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className={`text-center mb-6 sm:mb-10 transition-all duration-700 ${showcaseVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="inline-flex items-center gap-2 bg-[#D4A520]/15 border border-[#D4A520]/30 rounded-full px-4 py-1.5 mb-4">
              <span className="text-[#D4A520] text-xs font-semibold tracking-widest uppercase">Investment Showcase</span>
            </div>
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-[#0B1F3B] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Invest in Women-Led Businesses
            </h2>
            <p className="text-[#0B1F3B]/60 text-base sm:text-lg max-w-xl mx-auto">
              Discover vetted opportunities from women entrepreneurs changing industries.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 mb-8 sm:mb-10">
            {BUSINESSES?.map((biz, i) => (
              <div
                key={biz?.name}
                className={`group bg-white border border-[#0B1F3B]/10 rounded-2xl p-5 sm:p-6 cursor-pointer ${
                  showcaseVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{
                  transitionDelay: `${i * 100}ms`,
                  transition: 'opacity 500ms ease, transform 300ms ease-out, box-shadow 300ms ease-out',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(11,31,59,0.12)';
                  const crown = e?.currentTarget?.querySelector('.crown-badge');
                  if (crown) crown.style.transform = 'scale(1.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                  const crown = e?.currentTarget?.querySelector('.crown-badge');
                  if (crown) crown.style.transform = 'scale(1)';
                }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 rounded-xl bg-[#EEF1FA] flex items-center justify-center text-3xl">{biz?.emoji}</div>
                  <div className="flex items-center gap-1 bg-[#D4A520]/15 border border-[#D4A520]/30 rounded-full px-2.5 py-1">
                    <span
                      className="crown-badge text-[#D4A520] text-xs"
                      style={{ display: 'inline-block', transition: 'transform 300ms ease-out' }}
                    >👑</span>
                    <span className="text-[#D4A520] text-xs font-semibold">WIN Verified</span>
                  </div>
                </div>
                <h3 className="text-[#0B1F3B] font-bold text-lg mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{biz?.name}</h3>
                <div className="flex gap-2 mb-3">
                  <span className="bg-[#EEF1FA] text-[#0B1F3B] text-xs px-2.5 py-1 rounded-full font-medium">{biz?.sector}</span>
                  <span className="bg-[#0B1F3B]/8 text-[#0B1F3B] text-xs px-2.5 py-1 rounded-full font-medium">{biz?.stage}</span>
                </div>
                <p className="text-[#0B1F3B]/60 text-sm leading-relaxed">{biz?.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate('/onboarding-authentication')}
              className="relative inline-flex items-center gap-2 px-8 py-3.5 text-[#0B1F3B] font-bold rounded-xl overflow-hidden"
              style={{
                background: '#D4A520',
                transition: 'transform 250ms ease-out, box-shadow 250ms ease-out, background 250ms ease-out',
                boxShadow: '0 4px 16px rgba(212,165,32,0.2)',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.04)';
                e.currentTarget.style.background = '#E8C040';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(212,165,32,0.5)';
                const shine = e?.currentTarget?.querySelector('.showcase-shine');
                if (shine) { shine.style.transform = 'translateX(100%)'; shine.style.transition = 'transform 500ms ease'; }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.background = '#D4A520';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(212,165,32,0.2)';
                const shine = e?.currentTarget?.querySelector('.showcase-shine');
                if (shine) { shine.style.transform = 'translateX(-100%)'; shine.style.transition = 'none'; }
              }}
              onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; }}
              onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1.04)'; }}
            >
              <span className="showcase-shine absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.4) 50%, transparent 60%)', transform: 'translateX(-100%)' }} />
              <span className="relative z-10 flex items-center gap-2">Browse All Opportunities <span>→</span></span>
            </button>
          </div>
        </div>
      </section>
      {/* WIN Academy Preview */}
      <section id="academy" className="py-8 sm:py-14 bg-[#0B1F3B]" ref={academyRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className={`text-center mb-6 sm:mb-10 transition-all duration-700 ${academyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="inline-flex items-center gap-2 bg-[#D4A520]/15 border border-[#D4A520]/30 rounded-full px-4 py-1.5 mb-4">
              <span className="text-[#D4A520] text-xs font-semibold tracking-widest uppercase">WIN Academy</span>
            </div>
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-[#D4A520] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Learn From the Best
            </h2>
            <p className="text-white text-base sm:text-lg max-w-xl mx-auto">
              Expert-led courses designed to take you from beginner to confident investor.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-8 sm:mb-10">
            {COURSES?.map((course, i) => (
              <div
                key={course?.title}
                className={`relative bg-white/5 border border-white/10 rounded-2xl p-5 sm:p-6 cursor-pointer overflow-hidden ${
                  academyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{
                  transitionDelay: `${i * 100}ms`,
                  transition: 'opacity 500ms ease, transform 300ms ease-out, box-shadow 300ms ease-out, background 300ms ease-out',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px)';
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.2)';
                  const topBar = e?.currentTarget?.querySelector('.course-top-bar');
                  if (topBar) topBar.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                  e.currentTarget.style.boxShadow = 'none';
                  const topBar = e?.currentTarget?.querySelector('.course-top-bar');
                  if (topBar) topBar.style.opacity = '0';
                }}
              >
                {/* Gold top accent bar — brightens on hover */}
                <div
                  className="course-top-bar absolute top-0 left-0 right-0 h-0.5 bg-[#D4A520] rounded-t-2xl"
                  style={{ opacity: 0, transition: 'opacity 300ms ease-out' }}
                />
                <div className="text-4xl mb-4">{course?.emoji}</div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${LEVEL_COLORS?.[course?.level]}`}>
                  {course?.level}
                </span>
                <h3 className="text-white font-bold text-lg mt-3 mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{course?.title}</h3>
                <p className="text-white text-xs mb-4">{course?.lessons} lessons</p>
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-[#D4A520] rounded-full" style={{ width: '0%' }} />
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate('/onboarding-authentication')}
              className="relative inline-flex items-center gap-2 px-8 py-3.5 font-bold rounded-xl overflow-hidden"
              style={{
                border: '2px solid #D4A520',
                color: '#D4A520',
                background: 'transparent',
                transition: 'transform 250ms ease-out, box-shadow 250ms ease-out',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.04)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(212,165,32,0.3)';
                const fill = e?.currentTarget?.querySelector('.academy-fill');
                if (fill) { fill.style.transform = 'translateX(0)'; }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
                const fill = e?.currentTarget?.querySelector('.academy-fill');
                if (fill) { fill.style.transform = 'translateX(-100%)'; }
              }}
              onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; }}
              onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1.04)'; }}
            >
              <span className="academy-fill absolute inset-0 pointer-events-none rounded-xl" style={{ background: 'rgba(212,165,32,0.1)', transform: 'translateX(-100%)', transition: 'transform 300ms ease-out' }} />
              <span className="relative z-10 flex items-center gap-2">Explore WIN Academy <span>→</span></span>
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default ShowcaseSection;
